import { create } from "zustand";
import { reviewApi } from "../services/review-api";
import type { ReviewRequest, ReviewResponse, ReviewState, ReviewUpdateRequest, ReviewPaginatedResponse, ReviewFilters, ReviewSort, ReviewStatusCounts } from "../types";

export type PlanLimitErrorInfo = {
	feature: string;
	limit: number;
	current_count: number;
	message: string;
};

function throwPlanLimitError(error: PlanLimitErrorInfo): never {
	const err = new Error(error.message) as Error & { isPlanLimit: true; planLimitInfo: PlanLimitErrorInfo };
	err.isPlanLimit = true;
	err.planLimitInfo = error;
	throw err;
}

export const useReviewStore = create<ReviewState>(() => ({
	selectedReview: null,
	reviews: null,

	index: async (workspaceId: string, cursor?: string | null, filters?: ReviewFilters): Promise<ReviewPaginatedResponse | null> => {
		const result = await reviewApi.index(workspaceId, cursor, filters);

		if (result.success && result.data) {
			return result.data;
		}

		return null;
	},

	indexForWallOfLove: async (workspaceId: string, cursor?: string | null, filters?: ReviewFilters, sort?: ReviewSort, locallyPinnedReviewIds?: string[]): Promise<ReviewPaginatedResponse | null> => {
		const result = await reviewApi.indexForWallOfLove(workspaceId, cursor, filters, sort, locallyPinnedReviewIds);

		if (result.success && result.data) {
			return result.data;
		}

		return null;
	},

	create: async (workspaceId: string, request: ReviewRequest): Promise<ReviewResponse | null> => {
		const result = await reviewApi.create(workspaceId, request);
		if (result.success && result.data) {
			return result.data;
		}
		if (result.status === 402 && result.error?.error === "plan_limit_exceeded") {
			throwPlanLimitError({
				feature: result.error.feature,
				limit: result.error.limit,
				current_count: result.error.current_count,
				message: result.message || "Plan limit exceeded",
			});
		}
		return null;
	},

	show: async (workspaceId: string, reviewId: string): Promise<ReviewResponse | null> => {
		const result = await reviewApi.show(workspaceId, reviewId);
		if (result.success && result.data) {
			return result.data;
		}
		return null;
	},

	getByIds: async (workspaceId: string, ids: string[]) => {
		const result = await reviewApi.getByIds(workspaceId, ids);
		if (result.success && result.data) {
			return result.data;
		}
		return null;
	},

	update: async (workspaceId: string, id: string, request: ReviewUpdateRequest) => {
		const result = await reviewApi.update(workspaceId, id, request);

		if (result.success && result.data) {
			return result.data;
		}

		if (result.status === 402 && result.error?.error === "plan_limit_exceeded") {
			throwPlanLimitError({
				feature: result.error.feature,
				limit: result.error.limit,
				current_count: result.error.current_count,
				message: result.message || "Plan limit exceeded",
			});
		}

		return null;
	},

	delete: async (workspaceId: string, id: string) => {
		const result = await reviewApi.delete(workspaceId, id);

		if (result.success) {
			return true;
		}

		return null;
	},

	approve: async (workspaceId: string, reviewId: string): Promise<ReviewResponse | null> => {
		const result = await reviewApi.approve(workspaceId, reviewId);

		if (result.success && result.data) {
			return result.data;
		}

		return null;
	},

	unapprove: async (workspaceId: string, reviewId: string): Promise<ReviewResponse | null> => {
		const result = await reviewApi.unapprove(workspaceId, reviewId);

		if (result.success && result.data) {
			return result.data;
		}

		return null;
	},

	statusCounts: async (workspaceId: string, filters?: ReviewFilters): Promise<ReviewStatusCounts | null> => {
		const result = await reviewApi.statusCounts(workspaceId, filters);

		if (result.success && result.data) {
			return result.data;
		}

		return null;
	},

	indexDrafts: async (workspaceId: string, cursor?: string | null): Promise<ReviewPaginatedResponse | null> => {
		const result = await reviewApi.indexDrafts(workspaceId, cursor);

		if (result.success && result.data) {
			return result.data;
		}

		return null;
	},
}));
